import { ID } from '@/types/model'
import useUtilityPrograms from '@/app/hooks/useUtilityPrograms'
import { useMemo } from 'react'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { MappedUtilityProgram } from '@/reducers/programsNew'
import { match } from 'ts-pattern'
import useProfileId from '@/hooks/useProfileId'
import { Thermostat, Vehicle, VehicleCharger } from '@/types'

function getEligibleDevicesOfType(
  deviceType: DeviceType,
  program: MappedUtilityProgram,
  profileId?: number,
) {
  const { devices: eligibilityOverview } = program.device_eligibility_overview

  if (!program.enrollment_overview) {
    return []
  }

  const {
    vehicle_enrollments,
    charger_enrollments,
    thermostat_enrollments,
    behavioral_enrollments,
  } = program.enrollment_overview

  const enrolledIds = new Set(
    match(deviceType)
      .with('vehicle', () =>
        vehicle_enrollments
          .filter(
            (e) =>
              Number.parseInt(e.utility_program_id) === program.id &&
              e.enrolled,
          )
          .map((e) => e.vehicle_id),
      )
      .with('vehicleCharger', () =>
        charger_enrollments
          .filter(
            (e) =>
              Number.parseInt(e.utility_program_id) === program.id &&
              e.enrolled,
          )
          .map((e) => e.charger_id),
      )
      .with('thermostat', () =>
        thermostat_enrollments
          .filter(
            (e) =>
              Number.parseInt(e.utility_program_id) === program.id &&
              e.enrolled,
          )
          .map((e) => e.thermostat_id),
      )
      .with('home', () =>
        behavioral_enrollments
          .filter(
            (e) =>
              Number.parseInt(e.utility_program_id) === program.id &&
              e.enrolled,
          )
          .map((e) => e.profile_id),
      )
      .with('energySystem', () => []) // Not Yet Implemented
      .exhaustive(),
  )

  const isEligibleAndNotEnrolled = <T extends { id: number }>(
    item: T | null | undefined,
  ): item is T => !!item && !enrolledIds.has(item.id)

  return match(deviceType)
    .with('vehicle', () => {
      if (!eligibilityOverview.vehicles) {
        return []
      }

      return eligibilityOverview.vehicles
        .map((ve) => (ve.is_eligible ? ve.vehicle : null))
        .filter(isEligibleAndNotEnrolled)
    })
    .with('vehicleCharger', () => {
      if (!eligibilityOverview.chargers) {
        return []
      }

      return eligibilityOverview.chargers
        .map((ce) => (ce.is_eligible ? ce.charger : null))
        .filter(isEligibleAndNotEnrolled)
    })
    .with('thermostat', () => {
      if (!eligibilityOverview.thermostats) {
        return []
      }

      return eligibilityOverview.thermostats
        .map((te) => (te.is_eligible ? te.thermostat : null))
        .filter(isEligibleAndNotEnrolled)
    })
    .with('home', () => {
      if (!eligibilityOverview.behavioral) {
        return []
      }

      const isEligible = eligibilityOverview.behavioral.some(
        (be) => be.is_eligible,
      )
      return isEligible &&
        profileId !== undefined &&
        !enrolledIds.has(profileId)
        ? [profileId]
        : []
    })
    .with('energySystem', () => {
      return [] // Not yet implemented
    })
    .exhaustive()
}

type EnrollableDevices = {
  enrollableVehicles: Vehicle[]
  enrollableChargers: VehicleCharger[]
  enrollableThermostats: Thermostat[]
  enrollableProfiles: ID[]
  enrollableDeviceCount: number
  isLoading: boolean
}

export default function useEnrollableDevices(programId: ID) {
  const { enrolledPrograms, isLoading } = useUtilityPrograms({
    onlyMultiPrograms: true,
  })
  const profileId = useProfileId()

  const relevantProgram = useMemo(() => {
    return enrolledPrograms.find((p) => p.id === programId)
  }, [enrolledPrograms, programId])

  if (!relevantProgram) {
    return {
      enrollableVehicles: [],
      enrollableChargers: [],
      enrollableThermostats: [],
      enrollableProfiles: [],
      enrollableDeviceCount: 0,
      isLoading,
    }
  }

  const enrollableVehicles = getEligibleDevicesOfType(
    'vehicle',
    relevantProgram,
    profileId ?? undefined,
  ) as Vehicle[]
  const enrollableChargers = getEligibleDevicesOfType(
    'vehicleCharger',
    relevantProgram,
    profileId ?? undefined,
  ) as VehicleCharger[]
  const enrollableThermostats = getEligibleDevicesOfType(
    'thermostat',
    relevantProgram,
    profileId ?? undefined,
  ) as Thermostat[]
  const enrollableProfiles = getEligibleDevicesOfType(
    'home',
    relevantProgram,
    profileId ?? undefined,
  ) as ID[]

  return {
    enrollableVehicles,
    enrollableChargers,
    enrollableThermostats,
    enrollableProfiles,
    enrollableDeviceCount:
      [
        ...enrollableVehicles,
        ...enrollableChargers,
        ...enrollableThermostats,
        ...enrollableProfiles,
      ]?.length ?? 0,
    isLoading,
  }
}
